/**
 * USE NOTIFICATIONS HOOK
 *
 * React hook for managing in-app notifications with Supabase realtime
 * Provides real-time updates, mark as read functionality, and unread counts
 */

import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from './use-auth';
import type { RealtimeChannel } from '@supabase/supabase-js';

// =====================================================
// TYPES
// =====================================================

export interface Notification {
  id: string;
  user_id: string;
  type: string;
  title: string;
  message: string;
  organization_id: string | null;
  location_id: string | null;
  task_id: string | null;
  deactivation_queue_id: string | null;
  priority: 'low' | 'medium' | 'high' | 'critical';
  read: boolean;
  read_at: string | null;
  action_url: string | null;
  metadata: Record<string, any> | null;
  created_at: string;
  updated_at: string;
}

interface UseNotificationsReturn {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  error: Error | null;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refetch: () => Promise<void>;
}

// =====================================================
// HOOK
// =====================================================

export function useNotifications(limit: number = 50): UseNotificationsReturn {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [channel, setChannel] = useState<RealtimeChannel | null>(null);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Fetch recent notifications
      const { data, error: fetchError } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (fetchError) throw fetchError;

      setNotifications((data || []) as Notification[]);
      setUnreadCount((data || []).filter((n) => !n.read).length);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch notifications'));
    } finally {
      setIsLoading(false);
    }
  }, [user, limit]);

  // Mark notification as read
  const markAsRead = useCallback(
    async (notificationId: string) => {
      if (!user) return;

      try {
        const { error: updateError } = await supabase.rpc('mark_notification_read', {
          notification_id: notificationId,
        });

        if (updateError) throw updateError;

        // Update local state
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notificationId
              ? { ...n, read: true, read_at: new Date().toISOString() }
              : n
          )
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch (err) {
        console.error('Error marking notification as read:', err);
        throw err;
      }
    },
    [user]
  );

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    if (!user) return;

    try {
      const { error: updateError } = await supabase.rpc('mark_all_notifications_read');

      if (updateError) throw updateError;

      // Update local state
      const now = new Date().toISOString();
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, read: true, read_at: n.read_at || now }))
      );
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      throw err;
    }
  }, [user]);

  // Set up realtime subscription
  useEffect(() => {
    if (!user) {
      // Clean up if user logs out
      if (channel) {
        supabase.removeChannel(channel);
        setChannel(null);
      }
      return;
    }

    // Initial fetch
    fetchNotifications();

    // Set up realtime subscription for new notifications
    const realtimeChannel = supabase
      .channel(`notifications:user_id=eq.${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const newNotification = payload.new as Notification;
          setNotifications((prev) => [newNotification, ...prev].slice(0, limit));
          if (!newNotification.read) {
            setUnreadCount((prev) => prev + 1);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const updatedNotification = payload.new as Notification;
          setNotifications((prev) =>
            prev.map((n) => (n.id === updatedNotification.id ? updatedNotification : n))
          );

          // Recalculate unread count
          setNotifications((prev) => {
            setUnreadCount(prev.filter((n) => !n.read).length);
            return prev;
          });
        }
      )
      .subscribe();

    setChannel(realtimeChannel);

    // Cleanup
    return () => {
      if (realtimeChannel) {
        supabase.removeChannel(realtimeChannel);
      }
    };
  }, [user, limit, fetchNotifications]);

  return {
    notifications,
    unreadCount,
    isLoading,
    error,
    markAsRead,
    markAllAsRead,
    refetch: fetchNotifications,
  };
}

// =====================================================
// HELPER FUNCTIONS
// =====================================================

/**
 * Format notification time relative to now
 */
export function formatNotificationTime(timestamp: string): string {
  const now = new Date();
  const time = new Date(timestamp);
  const diffInSeconds = Math.floor((now.getTime() - time.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'Just now';
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  } else if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} day${days === 1 ? '' : 's'} ago`;
  } else {
    return time.toLocaleDateString();
  }
}

/**
 * Get icon for notification type
 */
export function getNotificationIcon(type: string): string {
  switch (type) {
    case 'IT_TASKS_CREATED':
    case 'IT_TASK_COMPLETED':
    case 'IT_PHASE_COMPLETE':
    case 'IT_DECONFIG_TASK_CREATED':
    case 'IT_DECONFIG_COMPLETED':
      return '🔧';
    case 'DATA_TASKS_CREATED':
    case 'DATA_TASK_COMPLETED':
    case 'DATA_PHASE_COMPLETE':
    case 'DATA_DECONFIG_TASK_CREATED':
      return '📊';
    case 'REVIEW_REQUEST_CREATED':
    case 'DEACTIVATION_REVIEW_CREATED':
      return '📋';
    case 'ORGANIZATION_APPROVED':
    case 'ORGANIZATION_ACTIVATED':
    case 'LOCATION_ACTIVATED':
      return '✅';
    case 'ORGANIZATION_REJECTED':
    case 'DEACTIVATION_DISAPPROVED':
      return '❌';
    case 'READY_FOR_ACTIVATION':
      return '🚀';
    case 'DEACTIVATION_APPROVED':
    case 'DEACTIVATION_COMPLETED':
    case 'DECONFIG_TASKS_CREATED':
      return '🔴';
    default:
      return '🔔';
  }
}

/**
 * Get priority color class
 */
export function getPriorityColor(priority: Notification['priority']): string {
  switch (priority) {
    case 'critical':
      return 'text-red-600 bg-red-100 dark:bg-red-900/20 dark:text-red-400';
    case 'high':
      return 'text-orange-600 bg-orange-100 dark:bg-orange-900/20 dark:text-orange-400';
    case 'medium':
      return 'text-blue-600 bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400';
    case 'low':
      return 'text-gray-600 bg-gray-100 dark:bg-gray-800 dark:text-gray-400';
    default:
      return 'text-gray-600 bg-gray-100 dark:bg-gray-800 dark:text-gray-400';
  }
}
